import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { getLocale } from '@hub/libs/constants';
import { Link } from '@hub/common/Link/Link';
import { TextInput } from '@hub/common/TextInput/TextInput';
import { IconButton } from '@hub/common/IconButton/IconButton';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import styles from '../ContactPersonBox/ContactPersonBox.module.scss';
import classNames from 'classnames';
import { getContactPersonsByZipCode } from '@hub/libs/catalog-service';
import type { ContactPerson } from '@hub/models/graphql';
import { openPopupWin } from '@hub/libs/ui.helper';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';
import { getLanguageAlternates } from '@hub/libs/i18n.helper';
import { EXTERNAL_LINKS, IGUS_REWRITE } from '@hub/libs/redirect.helper';

export const ContactPersonBoxPostCodeAreaType = () => {
  const [zipCode, setZipCode] = useState('');
  const [contactPersonsResults, setContactPersonsResults] = useState<ContactPerson[]>([]);
  const { t } = useTranslation('layout/contactPersonBox');
  const router = useRouter();
  const locale = getLocale(router);
  const defaultUrl = getLanguageAlternates().find(alternate => alternate.hrefLang === locale)?.href;

  useEffect(() => {
    const localStorageZipCode = localStorage.getItem('zipCode');
    if (localStorageZipCode && !zipCode) {
      setZipCode(localStorageZipCode);
    }
    const localStorageContacts: string = localStorage.getItem('zipCodeContacts') ?? '';
    if (localStorageContacts) {
      const contacts = JSON.parse(localStorageContacts) as unknown as ContactPerson[];
      if (contacts && contacts.length > 0 && contactPersonsResults.length === 0) {
        setContactPersonsResults(contacts);
      }
    }
  }, []);

  const getContactPersonsZipCode = async () => {
    if (!zipCode || zipCode.length < 2) {
      return;
    }
    try {
      await getContactPersonsByZipCode(zipCode, defaultUrl).then(results => {
        setContactPersonsResults(results);
        localStorage.setItem('zipCodeContacts', JSON.stringify(results));
      });
    } catch (error) {
      Sentry.captureException(new Error(`Could not getContactPersonsByZipCode - ${error as string}`));
    }
  };

  const openWriteContactPerson = (link: string) => () => openPopupWin(link, 'CONTACT', true, false, 400, 525);
  return (
    <div
      data-testid="post-code-area-type"
      className={classNames(styles.box, 'border', 'border-gray-200', 'absolute', 'px-12', 'bg-white', 'z-50', 'pb-12')}
    >
      {contactPersonsResults?.length > 0 ? (
        contactPersonsResults.map(contact => (
          <div className="flex gap-4 flex-col px-12 py-12 border-b border-gray-100" key={contact.name}>
            <h6 className="mb-4 text-xl">{contact.businessUnit}</h6>
            <div className="flex flex-row gap-2 items-center">
              <div>
                <p className="text-s">{contact.name}</p>
                {contact.phone && (
                  <p className="text-xs mt-2">
                    <Icon size={4} primary={false} id={IconID.PHONE} />
                    {contact.phone}
                  </p>
                )}
              </div>
              {contact.photo && (
                <img
                  loading="lazy"
                  alt={`${contact.businessUnit || ''} ${contact.name}`}
                  className="rounded-full h-20 w-20 float-right"
                  src={IGUS_REWRITE(contact.photo, locale)}
                />
              )}
            </div>
            {contact.messageParams?.length === 3 && (
              <Link
                data-testid="link-message"
                text={t('CONTACT_PERSON_BOX.WRITE_MESSAGE')}
                onClick={openWriteContactPerson(
                  EXTERNAL_LINKS.CONTACT_PERSON.WRITE_MESSAGE(
                    contact.messageParams[0],
                    contact.messageParams[1],
                    contact.messageParams[2],
                    locale
                  )
                )}
                icon={IconID.ARROW_RIGHT}
                external
                bigIcon={false}
                iconPrimary
                className="text-xxs"
                primary={false}
              />
            )}
          </div>
        ))
      ) : (
        <div className="mt-12">
          <h6 className="mb-4 text-xl">{t('CONTACT_PERSON_BOX.SELECT_LOCATION.TITLE')}</h6>
          <p>{t('CONTACT_PERSON_BOX.SELECT_LOCATION.HINT')}</p>
          <div className="flex flex-row my-6 border">
            <div className="flex-grow text-gray-400">
              <TextInput
                value={zipCode}
                onBlur={getContactPersonsZipCode}
                onChange={event => {
                  localStorage.setItem('zipCode', event.replace(/\s/g, ''));
                  setZipCode(event.replace(/\s/g, ''));
                }}
                placeholder={t('CONTACT_PERSON_BOX.SELECT_LOCATION.INPUT_PLACEHOLDER')}
                autoComplete="none"
              />
            </div>
            <IconButton icon={IconID.SEARCH} onClick={getContactPersonsZipCode} />
          </div>
        </div>
      )}
      <div>
        <Link
          text={t('CONTACT_PERSON_BOX.SEE_ALL_CONTACT')}
          icon={IconID.ARROW_RIGHT}
          bigIcon={false}
          href={EXTERNAL_LINKS.CONTACT_PERSON.SEE_ALL_CONTACT(locale)}
          className="text-xxs"
          primary
        />
      </div>
    </div>
  );
};
